<template>
  <CustomDropdown
    ref="dropdownRef"
    :maxHeight="maxHeight"
    gap="10px"
    position="start"
    dropdownCSS="p-0"
    width="w-fit"
    :relativeToViewport="relativeToViewport"
  >
    <template #trigger>
      <div class="flex items-center gap-1">
        <div
          v-if="field.type === 'user' && selectedOptions.length > 0 && variant === '1'"
          :class="[triggerClasses]"
        >
          <AvatarStack
            :items="selectedOptions"
            :max-visible="1"
            :size="20"
            :font-size="9"
            :border-radius="50"
            :overlap="8"
          />
        </div>

        <div
          v-else
          :class="[triggerClasses, 'proxima_nova text-[13px]', hasSelection ? 'font-semibold text_color' : 'font-normal']"
        >
          {{ hasSelection ? selectedLabel : field.name }}
          <span v-if="multiple && selectedOptions.length > 1"> {{ selectedOptions.length }}</span>

        <ThemeImage
          v-if="showArrow"
          :icon="imageNames.BOTTOM_ARROW_GREY"
          alt="arrow_down"
          :class="['size-3', 'arrow_icon', dropdownRef?.isOpen ? 'arrow_rotated' : '']"
        />
        </div>

      </div>
    </template>

    <template #default>
      <DateTimeDropdownOptions
        v-if="field.type === 'datetime' || field.type === 'date'"
        :field-type="field.type"
        :selected-value="modelValue"
        @select="handleSelect"
      />

      <DropdownOptions
        v-else
        :options="field.options || []"
        :selected-values="
          Array.isArray(modelValue)
            ? modelValue
            : modelValue
              ? [modelValue]
              : []
        "
        :show-search="
          showSearch && (field.type === 'user' || (field.options && field.options.length > 5))
        "
        :search-placeholder="
          field.type === 'user'
            ? 'Search users'
            : `Search ${field.name.toLowerCase()}`
        "
        :show-avatar="field.type === 'user'"
        :show-unselect-all="multiple"
        :no-results-text="`No ${field.name.toLowerCase()} found`"
        :multiple="multiple"
        :max-height="maxHeight"
        @select="handleSelect"
        @unselect-all="handleUnselectAll"
      />
    </template>
  </CustomDropdown>
</template>

<script setup>
import { computed, ref } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import DropdownOptions from "@/src/components/common/DropdownOptions.vue";
import DateTimeDropdownOptions from "@/src/components/common/DateTimeDropdownOptions.vue";
import AvatarStack from "@/src/components/common/AvatarStack.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { dateTimeFormats, formatTypes } from "@/src/constants/dateTimeConstants.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const dropdownRef = ref(null);

const props = defineProps({
  field: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: [Array, String, Number, Object],
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  showArrow: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: '1',
    validator: (value) => ['1', '2'].includes(value),
  },
  maxHeight: {
    type: String,
    default: '400px',
  },
  relativeToViewport: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const hasSelection = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.length > 0;
  }
  return props.modelValue != null && props.modelValue !== "";
});

const selectedOptions = computed(() => {
  if (!props.modelValue) return [];

  const values = Array.isArray(props.modelValue)
    ? props.modelValue
    : [props.modelValue];

  return values
    .map((val) => {
      if (typeof val === "object" && val !== null) return val;

      const option = props.field.options?.find((opt) => {
        if (typeof opt === "string") {
          return opt === val;
        }
        return (
          opt.id === val ||
          opt.value === val ||
          opt.label === val ||
          opt.name === val ||
          String(opt.id) === String(val) ||
          String(opt.value) === String(val)
        );
      });

      return (
        option ||
        (typeof val === "string"
          ? { id: val, name: val, label: val, value: val }
          : null)
      );
    })
    .filter(Boolean);
});

const selectedLabel = computed(() => {
  if (!hasSelection.value) return "";

  if (props.field.type === "date" || props.field.type === "datetime") {
    if (!props.modelValue) return "";

    if (typeof props.modelValue === "object" && props.modelValue.label) {
      return props.modelValue.label;
    }

    if (typeof props.modelValue === "string") {
      const date = props.field.type === "datetime"
        ? dayjs.utc(props.modelValue).local()
        : dayjs(props.modelValue);
      if (props.field.type === "datetime") {
        return date.isValid() ? date.format(`${dateTimeFormats[4]} ${formatTypes.time24Hour}`) : props.modelValue;
      }
      return date.isValid() ? date.format(dateTimeFormats[4]) : props.modelValue;
    }

    if (dayjs.isDayjs(props.modelValue)) {
      if (props.field.type === "datetime") {
        return props.modelValue.format(`${dateTimeFormats[4]} ${formatTypes.time24Hour}`);
      }
      return props.modelValue.format(dateTimeFormats[4]);
    }

    return String(props.modelValue);
  }

  if (Array.isArray(props.modelValue)) {
    return props.modelValue.length === 1
      ? selectedOptions.value[0]?.name ||
          selectedOptions.value[0]?.label ||
          selectedOptions.value[0] ||
          ""
      : `${props.modelValue.length} selected`;
  }

  const option = selectedOptions.value[0];
  if (!option) return String(props.modelValue);

  return (
    option?.name || option?.label || option?.value || String(props.modelValue)
  );
});

const triggerClasses = computed(() => {
  if (props.variant === '2') {
    return dropdownRef.value?.isOpen ? 'field_trigger_variant2_open' : 'field_trigger_variant2';
  }
  return hasSelection.value ? 'field_trigger_selected' : 'field_trigger_unselected';
});

const handleSelect = (option) => {
  if (props.multiple) {
    const currentValues = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];

    const optionValue =
      typeof option === "object"
        ? option.id || option.value || option.name
        : option;

    const index = currentValues.findIndex((val) => {
      const compareValue =
        typeof val === "object" ? val.id || val.value || val.name : val;

      return String(compareValue) === String(optionValue);
    });

    if (index > -1) {
      currentValues.splice(index, 1);
    } else {
      currentValues.push(option);
    }

    emit("update:modelValue", currentValues);
  } else {
    const optionValue =
      typeof option === "object"
        ? option.id || option.value || option.name
        : option;

    const currentValue =
      typeof props.modelValue === "object" && !Array.isArray(props.modelValue)
        ? props.modelValue?.id ||
          props.modelValue?.value ||
          props.modelValue?.name
        : props.modelValue;

    if (String(currentValue) === String(optionValue)) {
      emit("update:modelValue", null);
    } else {
      emit("update:modelValue", optionValue);
    }
  }
};

const handleUnselectAll = () => {
  emit("update:modelValue", props.multiple ? [] : null);
};

defineExpose({
  closeDropdown: () => dropdownRef.value?.closeDropdown(),
});
</script>

<style scoped>
.field_trigger_unselected {
  display: flex;
  height: 26px;
  padding: 0 8px;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px dashed var(--white-opacity-8);
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--placeholder-text-color);
}

.field_trigger_unselected:hover {
  border-color: var(--white-opacity-12);
  background-color: var(--white-opacity-2);
}

.field_trigger_selected {
  display: flex;
  height: 26px;
  padding: 0 8px;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-8);
  cursor: pointer;
  transition: all 0.2s ease;
}

.field_trigger_selected:hover {
  background: var(--white-opacity-12);
}

.multiselect_tag {
  display: inline-flex;
  padding: 2px 6px;
  align-items: center;
  border-radius: 4px;
  background: var(--white-opacity-4);
  color: var(--text-color);
  white-space: nowrap;
}

.multiselect_count {
  display: inline-flex;
  padding: 2px 6px;
  align-items: center;
  border-radius: 4px;
  background: var(--white-opacity-6);
  color: var(--secondary-color);
  white-space: nowrap;
}

.arrow_icon {
  transition: transform 0.3s ease;
}

.arrow_rotated {
  transform: rotate(180deg);
}

.field_trigger_variant2 {
  display: inline-flex;
  height: 28px;
  padding: 0 8px;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  box-sizing: border-box;
}

.field_trigger_variant2:hover {
  background-color: var(--white-opacity-4);
}

.field_trigger_variant2_open {
  display: inline-flex;
  height: 28px;
  padding: 0 8px;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  background-color: var(--white-opacity-4);
  transition: background-color 0.2s ease, border-color 0.2s ease;
  box-sizing: border-box;
}

.field_trigger_variant2_open:hover {
  background-color: var(--white-opacity-8);
}

@keyframes highlight {
  0% {
    background-color: var(--white-opacity-12);
  }
  50% {
    background-color: var(--white-opacity-16, rgba(255, 255, 255, 0.16));
  }
  100% {
    background-color: var(--white-opacity-12);
  }
}
</style>